import { type ComponentProps, type FC, memo, useCallback, useMemo, useState } from "react";
import { css, cx } from "@stubhub/broadway-foundation/css";
import { token } from "@stubhub/broadway-foundation/tokens";
import { IconButton, ButtonKind, ButtonSize, ButtonVariant } from "@stubhub/broadway-web";
import type { FlagCountry } from "@stubhub/broadway-web/icons/flags";
import {
  BaseballIcon,
  BasketballIcon,
  FootballIcon,
  HockeyIcon,
  NotificationIcon,
  SoccerIcon,
  TicketStarOutlineIcon,
} from "@stubhub/broadway-web/icons/functional";
import type { IconProps } from "@stubhub/broadway-web/icons/types";
import {
  EventGridListItem,
  EventGridListItemActions,
  EventGridListItemBody,
  EventGridListItemCalendar,
  EventGridListItemCalendarVariant,
  EventGridListItemCtas,
  EventGridListItemDateTime,
  EventGridListItemEventState,
  EventGridListItemEventType,
  EventGridListItemKind,
  EventGridListItemVariant,
  eventGridListItemContentRecipe,
  eventGridListItemCtasRecipe,
  useEventDateTime,
} from "@stubhub/ensemble-discovery";
import {
  Feature,
  RecordParticipationTypes,
  useFeatureParticipationRecorder,
  useFeatureTernary,
  useSimpleFeatureTernary,
} from "@viagogo/feature";
import { EventState, GridType } from "src/http/api-types";
import type { ShowtimeItem } from "src/http/types";
import { useContent } from "src/shared/components/content";
import { FeatureSetEnum } from "src/shared/constants/FeatureSetEnum";
import { ShowtimeChips } from "src/website-apps/responsive/stubhub-performer/components/calendar/mobile/residency";
import { usePerformerPageModelData } from "src/website-apps/responsive/stubhub-performer/hooks/usePerformerPageModelData";
import type { CommonEventGridListItem } from "src/website-apps/shared/components/common-event-grid-list-item/common-event-grid-list-item";
import { useCommonEventGridListItem } from "src/website-apps/shared/components/common-event-grid-list-item/use-common-event-grid-list-item";
import {
  InteractionType,
  useEventListAnalyticsEvent,
} from "src/website-apps/shared/components/event-grid-item/useEventListAnalyticsEvent";
import { MlbPlayoffCalendar } from "src/website-apps/shared/components/events-grid/MlbPlayoffCalendar";
import {
  mlbPlayoffActionsClasses,
  mlbPlayoffCardClasses,
} from "src/website-apps/shared/components/events-grid/MlbPlayoffs.styles";
import { useMlbPlayoffGame } from "src/website-apps/shared/components/events-grid/MlbPlayoffsContext";
import { TOP_LEVEL_CATEGORY_IDS } from "src/website-apps/shared/components/explore-events/constants";
import { NotificationFillIcon } from "src/website-apps/shared/components/FavoritesV2Buttons/FavoritesV2Button/icons/notification-fill";
import { InViewTracker } from "src/website-apps/shared/components/InViewTracker";
import { NotificationModal } from "src/website-apps/shared/components/on-sale-notifications/NotificationModal";
import { useIsIsWorldCupCategory } from "src/website-apps/shared/hooks/useIsWorldCupCategory";
import { useLeague } from "src/website-apps/shared/hooks/useLeague";
import { useSportsEventTitle } from "src/website-apps/shared/hooks/useSportsEventTitle";
import { getPerformerLeagueIdFromModel } from "src/website-apps/shared/utils/getPerformerLeagueIdFromModel";
import { DayContextTag } from "./day-context-tag/DayContextTag";
import { PerformerEventGridListItemBodyContent } from "./performer-event-grid-list-item-body-content";
import {
  performerEventGridListItemRecipe,
  performerEventGridListItemRecipeVariables,
} from "./performer-event-grid-list-item.recipe";
import { PlayoffCalendarBlock } from "./playoff-calendar-block/PlayoffCalendarBlock";

/**
 * Event grid list item component for the Performer page using Ensemble Discovery components.
 *
 * This component renders individual event cards in event grids on performer pages, migrated
 * from the legacy EventGridItem to use Ensemble's EventGridListItem component system.
 *
 * # Features
 * - **Sports title customization**: NBA/NFL/NHL show team abbreviations, MLB shows team logos
 * - **Season ticket icons**: League-specific icons for season ticket events
 * - **Notification modals**: Auto-opens from URL parameter, blocks StubHub UK events
 * - **Favorites/follow**: Integration with FavoritesContext for event following (experiment-gated removal via T-Performer-RemoveFavoritesFromEventList)
 * - **Search result tracking**: Algolia analytics for search results
 * - **Venue asset prefetching**: Experiment-based prefetching of venue assets
 * - **Event state handling**: Sold out, on sale soon, and active states
 * - **Analytics tracking**: Page events, UAE events, Broadway track integration
 * - **Back URL navigation**: Adds backUrl parameter for browser back button support
 * - **Playoff card**: The backend-owned playoff slot swaps the calendar block with PlayoffCalendarBlock
 *   and supplies the playoff rail color treatment.
 *
 * # Context
 * Requires these providers in the component tree:
 * - AppContext - Application state and configuration
 * - PageModelData - Performer page model data
 * - FavoritesContext - Favorites/follow functionality
 * - ContentContext - Internationalization content
 * - EnsembleProvider - Ensemble Discovery content provider
 *
 * @example
 * ```tsx
 * <PerformerEventGridListItem
 *   index={0}
 *   event={eventData}
 *   performerName="Taylor Swift"
 *   searchKeyWord="concert"
 *   onClick={(eventId, gridType) => handleClick(eventId, gridType)}
 *   gridType="upcoming-events"
 * />
 * ```
 *
 * @see useEventGridListItemPageEvents - Page events and analytics tracking
 * @see useNotificationModal - Notification modal state management
 * @see useEventUrlWithBackUrl - Event URL construction with back navigation
 * @see PerformerEventGridListItemBodyContent - Body content rendering (title, subtitle, tags)
 */

// categoryTypeId for MusicFestival grouping pages (e.g. Coachella, Lollapalooza).
// Used to exclude festival pages from the concert card hierarchy experiment —
// they share topLevelCategoryId=3 (Concert) with artist pages but are not artist pages.
// Value sourced from EventListControllerService.MusicFestivalCategoryTypeId.
const MUSIC_FESTIVAL_CATEGORY_TYPE_ID = 18;
const COUNTDOWN_TAG_ID = "COUNTDOWN";

const { onlyTabletDesktop } = eventGridListItemContentRecipe({ loadingState: "idle" });
const { seasonTicketIcon, seasonTicketIconContainer, dayContextTagOverlay } = performerEventGridListItemRecipe();

export const PerformerEventGridListItem: FC<ComponentProps<typeof CommonEventGridListItem>> = memo(
  ({ index, event, performerName, searchKeyWord, onClick, gridType, skeleton, usePlayoffPresentation = false }) => {
    const model = usePerformerPageModelData();
    const mlbPlayoff = useMlbPlayoffGame(event.eventId);
    /** "TBD" */
    const tbdContent = useContent("cid$#4399103#");
    const mlbPlayoffGame = mlbPlayoff?.game;

    // Compute before hooks — used to override gridType for residency grouped-events tiles
    const hasGroupedEvents = (event.groupedEvents?.length ?? 0) > 0;

    const effectiveGridType = hasGroupedEvents ? GridType.Residency : gridType;

    const leagueId = getPerformerLeagueIdFromModel(model);
    const { isMLB, isMLS, isNBA, isNFL, isNHL } = useLeague(leagueId);
    const isWorldCup = useIsIsWorldCupCategory();

    const { homeTeamPrimaryColor, homeTeamName, awayTeamName } = useSportsEventTitle(event.eventMetadata);
    const isHomeGame = event.eventMetadata?.sports?.homeTeamId === model.categorySummary.categoryId;
    const isAwayGame = event.eventMetadata?.sports?.awayTeamId === model.categorySummary.categoryId;

    // Playoff detection: backend populates playoffSeriesMetadata only for playoff events.
    const playoffSeriesMetadata = event.eventMetadata?.sports?.playoffSeriesMetadata;
    const isPlayoffEvent = !!playoffSeriesMetadata;
    const isSeasonTicket = !!event.eventMetadata?.sports?.isSeasonTicket;

    // Opponent TBD: the team that is NOT the performer's team is unconfirmed (no name).
    // When isHomeGame, the opponent is the away team; otherwise it is the home team.
    const isOpponentTbd = isHomeGame ? !!homeTeamName && !awayTeamName : !!awayTeamName && !homeTeamName;

    const execTeamVsTeamHomeEmphasis = useFeatureTernary(
      FeatureSetEnum.TMPerformerTeamVsTeamHomeEmphasis,
      () => (isNBA || isNFL || isNHL) && !!homeTeamPrimaryColor && isHomeGame,
    );

    const execHomeAwayTextIndicator = useFeatureTernary(
      FeatureSetEnum.TMPerformerHomeAwayTextIndicator,
      () => (isNBA || isNFL || isNHL || isMLB || isMLS) && (isHomeGame || isAwayGame),
    );
    const isTheater = model.categorySummary.topLevelCategoryId === TOP_LEVEL_CATEGORY_IDS.THEATER;
    const isSport = model.categorySummary.topLevelCategoryId === TOP_LEVEL_CATEGORY_IDS.SPORT;
    const isConcert = model.categorySummary.topLevelCategoryId === TOP_LEVEL_CATEGORY_IDS.CONCERT;

    const isMusicFestivalCategory = model.categorySummary.categoryTypeId === MUSIC_FESTIVAL_CATEGORY_TYPE_ID;

    const {
      appContext,
      isStubhub,
      isFavorited,
      isNotificationStored,
      favoritesEnabled,
      willRedirectToStubhubUk,
      eventUrl,
      eventState,
      venueLocation,
      onSaleTimeMillis,
      flagCountries,
      handleOnClick,
      handleOnHover,
      defaultTags,
      merchTags,
      merchInViewTrackers,
      handleOnInView: handleOnInViewBase,
      handleFollow,
      isNotificationModalOpen,
      handleOpenNotificationModal,
      handleCloseNotificationModal,
    } = useCommonEventGridListItem({
      event,
      gridType: effectiveGridType,
      index,
      onClick,
    });

    const isInTodayTomorrowBadgeVariant =
      useSimpleFeatureTernary(FeatureSetEnum.TMPerformerTodayTomorrowBadge, false, true) ?? false;

    const eventDateTime = useEventDateTime(
      isInTodayTomorrowBadgeVariant ? event.eventMetadata?.common?.eventStartDateTime : undefined,
      undefined,
      event.eventMetadata?.common?.venueTimeZoneOffset,
    );

    const dayContextKind = useMemo<"today" | "tomorrow" | null>(() => {
      if (!isInTodayTomorrowBadgeVariant || !eventDateTime.startTime) return null;
      if (eventDateTime.startsToday) return "today";
      if (eventDateTime.startsTomorrow) return "tomorrow";
      return null;
    }, [isInTodayTomorrowBadgeVariant, eventDateTime]);

    const effectiveDefaultTags = useMemo(
      () => (dayContextKind ? defaultTags.filter((tag) => tag.id !== COUNTDOWN_TAG_ID) : defaultTags),
      [defaultTags, dayContextKind],
    );

    const [sendShowtimeViewAnalytics] = useEventListAnalyticsEvent(event);
    const handleShowtimeInView = useCallback(
      (showtimeIndex: number) => {
        sendShowtimeViewAnalytics({
          merchTags: [],
          gridType: GridType.Residency,
          gridIndex: showtimeIndex,
          interactionType: InteractionType.VIEW,
          batchEvents: true,
        });
      },
      [sendShowtimeViewAnalytics],
    );

    const [activeShowtime, setActiveShowtime] = useState<ShowtimeItem | null>(null);

    const handleShowtimeSoldOutClick = useCallback(
      (item: ShowtimeItem) => {
        setActiveShowtime(item);
        handleOpenNotificationModal();
      },
      [handleOpenNotificationModal],
    );

    const handleCloseModal = useCallback(() => {
      handleCloseNotificationModal();
      setActiveShowtime(null);
    }, [handleCloseNotificationModal]);

    const seasonTicketIconContent = useMemo(() => {
      const iconProps = {
        size: 40,
        classes: { root: cx(seasonTicketIcon) },
        style: {
          [performerEventGridListItemRecipeVariables.seasonTicketIconPrimaryColor]:
            model.categoryMetadata?.sports?.partnerTheme?.secondaryColor ?? token("colors.text"),
        },
      } satisfies IconProps;

      if (!event.eventMetadata?.sports?.isSeasonTicket) {
        return null;
      }
      if (isMLB) {
        return <BaseballIcon {...iconProps} />;
      }
      if (isMLS) {
        return <SoccerIcon {...iconProps} />;
      }
      if (isNBA) {
        return <BasketballIcon {...iconProps} />;
      }
      if (isNFL) {
        return <FootballIcon {...iconProps} />;
      }
      if (isNHL) {
        return <HockeyIcon {...iconProps} />;
      }
      return <TicketStarOutlineIcon {...iconProps} />;
    }, [
      event.eventMetadata?.sports?.isSeasonTicket,
      model.categoryMetadata?.sports?.partnerTheme?.secondaryColor,
      isMLB,
      isMLS,
      isNBA,
      isNFL,
      isNHL,
    ]);

    const shouldFlipTitles = !isStubhub && (isTheater || isConcert);
    const canParticipateFlippedTitles = useCallback(() => shouldFlipTitles, [shouldFlipTitles]);
    const locationPriority =
      useSimpleFeatureTernary(
        FeatureSetEnum.TMPerformerViagogoStandardConcertEventTitles,
        shouldFlipTitles,
        false,
        canParticipateFlippedTitles,
      ) ?? shouldFlipTitles;

    // CWP-2524: Concert event card content hierarchy for tour, festival, and supporting act events
    // canParticipate: concert, non-residency, non-festival pages only
    const isResidency =
      model.performerGridSurface?.type === "residency" || model.performerGridSurface?.type === "calendar-residency";
    const canParticipateConcertHierarchy = useCallback(
      () => isConcert && !isResidency && !isMusicFestivalCategory,
      [isConcert, isResidency, isMusicFestivalCategory],
    );
    const useConcertHierarchy =
      useSimpleFeatureTernary(
        FeatureSetEnum.TMPerformerConcertEventCardHierarchy,
        false,
        true,
        canParticipateConcertHierarchy,
      ) ?? false;

    const concertEventNameAtFestival = event.eventMetadata?.concerts?.eventNameAtFestival ?? null;
    const concertEventNameWithSupport = event.eventMetadata?.concerts?.eventNameWithSupport ?? null;
    const concertEventName = concertEventNameWithSupport ?? concertEventNameAtFestival ?? null;

    // True for touring concert events in variant (no festival/support name) — switches the
    // title slot to VenueLocation and subtitle to Time + VenueName.
    const concertTouringLocationPriority = useConcertHierarchy && concertEventName === null;

    // CWP-2723: Festival card headliner subtitle — multivariate (variant 1: show headliners, variant 2: no subtitle)
    // canParticipate: festival events on concert, non-residency, non-festival-grouping pages only
    const canParticipateFestivalCardHierarchy = useCallback(
      () =>
        isConcert &&
        !isResidency &&
        !isMusicFestivalCategory &&
        !!concertEventNameAtFestival &&
        !!event.eventMetadata?.concerts?.festivalEventName,
      [
        isConcert,
        isResidency,
        isMusicFestivalCategory,
        concertEventNameAtFestival,
        event.eventMetadata?.concerts?.festivalEventName,
      ],
    );
    const execFestivalCardHierarchy = useFeatureTernary(
      FeatureSetEnum.TMPerformerFestivalEventCardHierarchy,
      canParticipateFestivalCardHierarchy,
    );
    const recordFestivalCardHierarchyInView = useFeatureParticipationRecorder(
      FeatureSetEnum.TMPerformerFestivalEventCardHierarchy,
      canParticipateFestivalCardHierarchy,
    );

    const { festivalEventName, festivalHeadliners, passLength } = execFestivalCardHierarchy({
      control: () => ({
        festivalEventName: null,
        festivalHeadliners: null,
        passLength: event.eventMetadata?.common?.passLength ?? undefined,
      }),
      variant: (_, variantNumber) => ({
        festivalEventName: event.eventMetadata?.concerts?.festivalEventName ?? null,
        festivalHeadliners: variantNumber === 1 ? (event.eventMetadata?.concerts?.headliners ?? null) : null,
        passLength:
          event.eventMetadata?.concerts?.isNonConsecutiveDates && (event.eventMetadata.concerts.passCount ?? 0) >= 3
            ? undefined
            : (event.eventMetadata?.concerts?.passCount ?? undefined),
      }),
      record: false,
    }) ?? {
      festivalEventName: null,
      festivalHeadliners: null,
      passLength: event.eventMetadata?.common?.passLength ?? undefined,
    };

    const baseShowFollow = favoritesEnabled && isStubhub && !event.isParkingEvent && !willRedirectToStubhubUk;
    const canParticipateRemoveFavorites = useCallback(() => baseShowFollow, [baseShowFollow]);
    const showFollow =
      useSimpleFeatureTernary(
        FeatureSetEnum.TPerformerRemoveFavoritesFromEventList,
        baseShowFollow,
        false,
        canParticipateRemoveFavorites,
      ) ?? baseShowFollow;

    // CWP-1929: icon-only notify pill on tablet/desktop — eligible on follow-eligible sold-out/coming-soon
    // tiles where the CTA block renders (grouped-events tiles only render it for playoff events).
    const canParticipateNotifyIconCta = useCallback(
      () =>
        baseShowFollow &&
        (eventState === EventGridListItemEventState.SoldOut || eventState === EventGridListItemEventState.ComingSoon) &&
        (!hasGroupedEvents || isPlayoffEvent),
      [baseShowFollow, eventState, hasGroupedEvents, isPlayoffEvent],
    );
    const showNotifyIcon =
      useSimpleFeatureTernary(
        FeatureSetEnum.TPerformerNotifyIconCta,
        false,
        true,
        canParticipateNotifyIconCta,
        false,
      ) ?? false;
    const recordNotifyIconCtaInView = useFeatureParticipationRecorder(
      FeatureSetEnum.TPerformerNotifyIconCta,
      canParticipateNotifyIconCta,
    );

    // The backend-owned playoff slot supersedes the general home-emphasis rail treatment.
    const playoffStartRailColor = usePlayoffPresentation ? homeTeamPrimaryColor : undefined;

    const homeOrAway: "home" | "away" | null =
      execHomeAwayTextIndicator({
        control: () => null,
        variant: () => (isSeasonTicket ? null : isHomeGame ? "home" : isAwayGame ? "away" : null),
      }) ?? null;

    const homeGameRailColor =
      execHomeAwayTextIndicator({
        control: () => undefined,
        variant: () => (isHomeGame ? (homeTeamPrimaryColor ?? undefined) : undefined),
      }) ?? undefined;

    const startRailColor =
      playoffStartRailColor ??
      homeGameRailColor ??
      execTeamVsTeamHomeEmphasis({
        control: () => undefined,
        variant: () => (isHomeGame ? (homeTeamPrimaryColor ?? undefined) : undefined),
      }) ??
      undefined;

    const baseKind = isSport ? EventGridListItemKind.Neutral : EventGridListItemKind.Brand;

    const controlCard = {
      cardVariant: EventGridListItemVariant.Outline,
      calendarVariant: undefined as EventGridListItemCalendarVariant | undefined,
      align: undefined as "center" | undefined,
      subtitleLayout: "standard" as const,
      showDateTime: false,
      darkCalendarDay: false,
    };

    const execModernizedCard = useFeatureTernary(FeatureSetEnum.MPerformerModernizedEventCard);
    const modernizedCard =
      execModernizedCard({
        control: () => controlCard,
        variant: (_, variantNumber) =>
          variantNumber === 1
            ? {
                cardVariant: EventGridListItemVariant.Ghost,
                calendarVariant: EventGridListItemCalendarVariant.Minimal,
                align: "center" as const,
                subtitleLayout: "two-line" as const,
                showDateTime: false,
                darkCalendarDay: false,
              }
            : variantNumber === 2
              ? {
                  cardVariant: EventGridListItemVariant.Ghost,
                  calendarVariant: EventGridListItemCalendarVariant.Flat,
                  align: "center" as const,
                  subtitleLayout: "single-line" as const,
                  showDateTime: false,
                  darkCalendarDay: false,
                }
              : controlCard,
      }) ?? controlCard;

    const execDesktopModernizedCard = useFeatureTernary(FeatureSetEnum.TPerformerModernizedEventCard);
    const desktopModernizedCard =
      execDesktopModernizedCard({
        control: () => controlCard,
        variant: (_, variantNumber) =>
          variantNumber === 1
            ? {
                cardVariant: EventGridListItemVariant.Ghost,
                calendarVariant: EventGridListItemCalendarVariant.Minimal,
                align: undefined,
                // "two-line" renders the one-line weekday · time | location · venue composition
                // on desktop breakpoints — the second line is mobile-visibility only
                subtitleLayout: "two-line" as const,
                showDateTime: false,
                darkCalendarDay: true,
              }
            : variantNumber === 2
              ? {
                  cardVariant: EventGridListItemVariant.Ghost,
                  calendarVariant: undefined,
                  align: undefined,
                  subtitleLayout: "location-line" as const,
                  showDateTime: true,
                  darkCalendarDay: false,
                }
              : controlCard,
      }) ?? controlCard;

    // Playoff cards stay control because the modernized layouts skip the playoff date; if both experiments resolve past control, the desktop arm wins.
    const effectiveCard =
      mlbPlayoffGame || usePlayoffPresentation
        ? controlCard
        : desktopModernizedCard === controlCard
          ? modernizedCard
          : desktopModernizedCard;

    const { listItem } = performerEventGridListItemRecipe();

    // Derive PlayoffCalendarBlock props from playoffSeriesMetadata.
    const playoffCalendarBlock = usePlayoffPresentation
      ? (() => {
          if (!playoffSeriesMetadata) return null;
          // TBA: no seriesGameNum AND no homeGameNum → date/opponent unknown
          if (playoffSeriesMetadata.seriesGameNum || playoffSeriesMetadata.homeGameNum) {
            // "Home N" — opponent unknown and seriesGameNum not yet deterministic.
            // If seriesGameNum is already set, seeding has fixed the game number regardless
            // of opponent — fall through to "Game N" in that case.
            if (isOpponentTbd && !playoffSeriesMetadata.seriesGameNum) {
              const homeGameNum = playoffSeriesMetadata.homeGameNum ?? undefined;
              return (
                <PlayoffCalendarBlock
                  eventState={eventState}
                  kind={baseKind}
                  variant="home"
                  gameNum={homeGameNum}
                  isContingent={playoffSeriesMetadata.isContingentGame ?? undefined}
                />
              );
            }
            // "Game N" — opponent confirmed, or seriesGameNum is already determined by seeding
            // (home/away is fixed even before the opponent is known).
            const seriesGameNum = playoffSeriesMetadata.seriesGameNum ?? playoffSeriesMetadata.homeGameNum ?? undefined;
            return (
              <PlayoffCalendarBlock
                eventState={eventState}
                kind={baseKind}
                variant="game"
                gameNum={seriesGameNum ?? undefined}
                isContingent={playoffSeriesMetadata.isContingentGame ?? undefined}
              />
            );
          }
          return (
            <PlayoffCalendarBlock
              eventState={eventState}
              kind={baseKind}
              variant="tbd"
              isContingent={playoffSeriesMetadata.isContingentGame ?? undefined}
            />
          );
        })()
      : null;

    // CWP-2420: When groupedEvents is present, suppress outer card navigation so individual
    // showtime buttons (rendered in the footer) each link to their own event page.
    // groupedEvents contains all showtimes for the day in chronological order (backend-sorted),
    // including the representative event itself as a shallow copy.

    return (
      <li key={event.eventId} className={listItem}>
        <InViewTracker
          onInView={() => {
            handleOnInViewBase();
            recordFestivalCardHierarchyInView();
            recordNotifyIconCtaInView();
          }}
        />
        <EventGridListItem
          data-mlb-playoff={mlbPlayoffGame ? "" : undefined}
          {...(hasGroupedEvents ? { hasMultipleShowtimes: true } : { href: eventUrl ?? "#", onClick: handleOnClick })}
          onMouseEnter={handleOnHover}
          kind={baseKind}
          variant={effectiveCard.cardVariant}
          align={effectiveCard.align}
          classes={mlbPlayoffGame ? mlbPlayoffCardClasses : undefined}
          searchQuery={searchKeyWord ?? undefined}
          eventState={eventState}
          eventType={event.isParkingEvent ? EventGridListItemEventType.Parking : EventGridListItemEventType.Event}
          eventTitle={
            festivalEventName ??
            concertEventName ??
            (concertTouringLocationPriority ? venueLocation : null) ??
            event.name
          }
          venueName={event.venueName ?? ""}
          venueLocation={venueLocation}
          venueCountryCode={event.countryCode as FlagCountry}
          startTimeMillis={event.eventMetadata?.common?.eventStartDateTime}
          endTimeMillis={event.eventMetadata?.common?.eventEndDateTime ?? undefined}
          venueTimeZoneOffsetMillis={event.eventMetadata?.common?.venueTimeZoneOffset}
          onSaleTimeMillis={onSaleTimeMillis}
          passLength={passLength}
          isDateConfirmed={event.isDateConfirmed}
          isTimeConfirmed={event.isTimeConfirmed}
          isPostponed={event.eventState === EventState.Postponed}
          fromPrice={mlbPlayoffGame ? undefined : (event.contextSensitiveFormattedMinPrice ?? undefined)}
          startRailColor={
            mlbPlayoffGame
              ? mlbPlayoffGame.isHomeGame
                ? (homeTeamPrimaryColor ?? undefined)
                : undefined
              : startRailColor
          }
          skeleton={skeleton}
        >
          {dayContextKind && (
            <div className={cx(dayContextTagOverlay, onlyTabletDesktop)}>
              <DayContextTag day={dayContextKind} device="desktop" />
            </div>
          )}
          {playoffCalendarBlock ??
            (seasonTicketIconContent ? (
              <div className={seasonTicketIconContainer}>{seasonTicketIconContent}</div>
            ) : mlbPlayoffGame ? (
              <MlbPlayoffCalendar dateConfirmed={event.isDateConfirmed} tbd={tbdContent} />
            ) : effectiveCard.showDateTime ? (
              <EventGridListItemDateTime />
            ) : (
              <EventGridListItemCalendar
                {...(effectiveCard.calendarVariant ? { calendarVariant: effectiveCard.calendarVariant } : {})}
                {...(effectiveCard.darkCalendarDay ? { classes: { contentPrimary: css({ color: "text" }) } } : {})}
              />
            ))}
          <EventGridListItemBody
            classes={hasGroupedEvents ? { container: css({ flexBasis: "[0px]" }) } : undefined}
            flagCountries={useConcertHierarchy ? undefined : flagCountries}
            showTime={hasGroupedEvents ? false : undefined}
          >
            {merchInViewTrackers}
            <PerformerEventGridListItemBodyContent
              event={event}
              isNBA={isNBA}
              isNFL={isNFL}
              isNHL={isNHL}
              isMLB={isMLB}
              isMLS={isMLS}
              isNationalTeam={isWorldCup}
              locationPriority={locationPriority}
              concertTouringLocationPriority={concertTouringLocationPriority}
              homeOrAway={homeOrAway}
              hideMerchTags={willRedirectToStubhubUk || hasGroupedEvents}
              subtitleLayout={effectiveCard.subtitleLayout}
              tags={effectiveDefaultTags}
              merchTags={hasGroupedEvents ? [] : merchTags}
              isPlayoffEvent={isPlayoffEvent}
              usePlayoffPresentation={usePlayoffPresentation}
              dayContextKind={dayContextKind}
              festivalHeadliners={festivalHeadliners}
            />
          </EventGridListItemBody>
          {(!hasGroupedEvents || isPlayoffEvent) && (
            <EventGridListItemActions classes={mlbPlayoffGame ? mlbPlayoffActionsClasses : undefined}>
              <Feature
                id={FeatureSetEnum.TMPerformerReplaceHeartWithNotificationIcon}
                recordType={RecordParticipationTypes.IN_VIEW}
                canParticipate={() => showFollow && eventState === EventGridListItemEventState.Active}
                control={() => (
                  <EventGridListItemCtas
                    isFollowed={isFavorited}
                    onFollowClick={handleFollow}
                    showFollow={showFollow}
                    showNotify={baseShowFollow}
                    showNotifyIcon={showNotifyIcon}
                    isNotified={isNotificationStored}
                  />
                )}
                variant={() => (
                  <>
                    <EventGridListItemCtas
                      isFollowed={isFavorited}
                      onFollowClick={handleFollow}
                      showFollow={false}
                      showNotify={baseShowFollow}
                      showNotifyIcon={showNotifyIcon}
                      isNotified={isNotificationStored}
                    />
                    {showFollow && eventState === EventGridListItemEventState.Active && (
                      <IconButton
                        aria-label={isFavorited ? "Unfollow" : "Follow"}
                        classes={{ root: eventGridListItemCtasRecipe().buttonHiddenMobileRoot ?? "" }}
                        icon={isFavorited ? NotificationFillIcon : NotificationIcon}
                        kind={ButtonKind.Neutral}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleFollow(e);
                        }}
                        size={ButtonSize.Small}
                        variant={ButtonVariant.Outline}
                      />
                    )}
                  </>
                )}
              />
            </EventGridListItemActions>
          )}
          {hasGroupedEvents && (
            <ShowtimeChips
              showtimes={event.groupedEvents ?? []}
              onSoldOutClick={handleShowtimeSoldOutClick}
              onShowtimeClick={handleOnClick}
              onShowtimeInView={handleShowtimeInView}
            />
          )}
        </EventGridListItem>
        <NotificationModal
          isOpen={isNotificationModalOpen}
          onClose={handleCloseModal}
          userFirstName={appContext.userFirstName ?? ""}
          userEmailAddress={appContext.userEmailAddress ?? ""}
          performerName={performerName}
          entityId={activeShowtime?.eventId ?? event.eventId}
          notificationModalContent={activeShowtime?.notificationModalContent ?? event.notificationModalContent}
          shouldEnableAdjustLinks
        />
      </li>
    );
  },
);
PerformerEventGridListItem.displayName = "PerformerEventGridListItem";
